import { apmBase } from '@elastic/apm-rum';
import axios from 'axios';
import throttle from 'lodash/throttle';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { IBetCount } from '@/interface/IBetCount';
import Api from '@/libraries/api';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export const useBetCountStore = defineStore('betCount', () => {
    const betCount = ref<IBetCount>({
        betBuilderBetCount: 0,
        cashOutBet: 0,
        cashOutMixParlayBet: 0,
        singleBet: 0,
        mixParlayBet: 0,
        outrightBet: 0,
    });
    const ticks = ref(0);
    const isBetCountLoaded = ref(false);

    const { refreshBetCountMinDuration } = storeToRefs(useFeatureToggleStore());

    const runningBetCount = computed(() => betCount.value.singleBet + betCount.value.mixParlayBet + betCount.value.outrightBet + betCount.value.betBuilderBetCount);
    const cashOutBetCount = computed(() => betCount.value.cashOutBet + betCount.value.cashOutMixParlayBet);

    const refreshBetCount = throttle((isUseCache) => {
        ticks.value = 0;
        getRunningBetCount(isUseCache);
    }, 2000);

    function tick() {
        ticks.value++;
        if (ticks.value >= refreshBetCountMinDuration.value && !(isBetCountLoaded.value && runningBetCount.value === 0)) {
            refreshBetCount(true);
        }
    }

    async function getRunningBetCount(isUseCache: boolean) {
        try {
            betCount.value = await Api.getRunningBetCount(isUseCache);
            isBetCountLoaded.value = true;
        } catch (e: any) {
            // The api interceptor appends " (request: METHOD URL)", so match the prefix only.
            // Not e.code: axios also reports timeouts as ECONNABORTED, and those must still retry.
            if (e?.message?.startsWith('Request aborted')) {
                return;
            }

            refreshBetCount(true);
            // HTTP failures are reported by the axios interceptor; anything else (e.g. a malformed body) would otherwise go unseen.
            if (!axios.isAxiosError(e)) {
                apmBase.captureError(e);
            }
        }
    }

    return {
        runningBetCount,
        cashOutBetCount,
        refreshBetCount,
        tick,
    };
});
